/* eslint-disable react-hooks/exhaustive-deps */
import { ChevronUpIcon } from "@heroicons/react/20/solid";
import {
  ActionIcon,
  Collapse,
  Flex,
  LoadingOverlay,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import uuid from "react-uuid";
import { useMemo, useState } from "react";
import { useAuthInfo } from "@propelauth/react";
import { queryClient, useWorkspaceMembers } from "../../../../hooks/QueryHooks";
import AddUserDropdown from "./AddUserDropdown";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";
import { assignKeyRole } from "../../../../services/companyEndpoint";

// Define proper type for a user
type User = {
  value: string;
  name?: string;
  email?: string;
  avatar_url?: string;
  label?: string;
};

type Props = {
  owner?: string,
  csm?: string,
  ae?: string,
  contacts?: any[],
  accountId: string,
  containerClass?: string,
  collapsible?: boolean,
  defaultOpen?: boolean,
  vertical?: boolean,
};

const KeyRole = ({
  owner, csm, ae, contacts, accountId, containerClass, collapsible = false,
  defaultOpen = true, vertical = false,
}: Props) => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  // Initialize with null, will find the actual user objects after lists are loaded
  const [customerSM, setCustomerSM] = useState<User | null>(null);
  const [accountE, setAccountE] = useState<User | null>(null);
  const [own, setOwn] = useState<User | null>(null);
  const { accessHelper } = useAuthInfo();
  const [opened, handlers] = useDisclosure(defaultOpen);
  const { data: workspaceMembers, isLoading }: any = useWorkspaceMembers({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
    retry: 2,
  });

  const csmList = useMemo(() => workspaceMembers?.members?.map((member: any) => ({
    value: member.user_id,
    label: `${member.first_name} ${member.last_name}`,
    name: `${member.first_name} ${member.last_name}`,
    email: member.email,
    avatar_url: member.avatar_url,
  })) || [], [workspaceMembers?.members?.length]);

  const ownerList = useMemo(() => contacts?.map((member: any) => ({
    value: member.external_id,
    label: member.name,
    name: member.name,
    email: member.email,
    avatar_url: member.avatar_url,
  })) || [], [contacts?.length]);

  const handleCsmSelect = async (user: User | null) => {
    setCustomerSM(user);
    if (!user) return; // Handle possible null value

    try {
      await assignKeyRole({
        account_id: accountId,
        assign_to_user_id: user.value, // Send the user ID value
        key_role_id: workspace?.key_roles?.find((role: any) => role.name === "CSM")?.id,
        workspace_identifier: workspace?.id,
      });
      queryClient.setQueryData(["account", workspace?.id, accountId], (oldData: any) => ({
        ...oldData,
        data: { ...oldData.data, csm_id: user.value },
      }));
      notify({
        messageList: ["Assigned CSM Successfully"],
        type: "success",
      });
    } catch {
      notify({
        messageList: ["Failed to assign CSM, please try again later"],
        type: "failure",
      });
    }
  };

  const handleAeSelect = async (user: User | null) => {
    setAccountE(user);
    if (!user) return; // Handle possible null value

    try {
      await assignKeyRole({
        account_id: accountId,
        assign_to_user_id: user.value, // Send the user ID value
        key_role_id: workspace?.key_roles?.find((role: any) => role.name === "Account Executive")?.id,
        workspace_identifier: workspace?.id,
      });
      queryClient.setQueryData(["account", workspace?.id, accountId], (oldData: any) => ({
        ...oldData,
        data: { ...oldData.data, ae_id: user.value },
      }));
      notify({
        messageList: ["Assigned AE Successfully"],
        type: "success",
      });
    } catch {
      notify({
        messageList: ["Failed to assign AE, please try again later"],
        type: "failure",
      });
    }
  };

  const handleOwnerSelect = async (user: User | null) => {
    setOwn(user);
    if (!user) return; // Handle possible null value

    try {
      await assignKeyRole({
        account_id: accountId,
        assign_to_user_id: user.value, // Send the user ID value
        key_role_id: workspace?.key_roles?.find((role: any) => role.name === "Account Owner")?.id,
        workspace_identifier: workspace?.id,
      });
      queryClient.setQueryData(["account", workspace?.id, accountId], (oldData: any) => ({
        ...oldData,
        data: { ...oldData?.data, owner_id: user.value },
      }));
      notify({
        messageList: ["Assigned Owner Successfully"],
        type: "success",
      });
    } catch {
      notify({
        messageList: ["Failed to assign Owner, please try again later"],
        type: "failure",
      });
    }
  };

  // Set initial users once the lists are loaded
  useMemo(() => {
    // Set owner if not already set and we have the owner ID
    if (!own && owner && ownerList?.length) {
      const foundOwner = ownerList.find((member: any) => member.value === owner);
      if (foundOwner) setOwn(foundOwner);
    }
  }, [own, owner, ownerList]);

  useMemo(() => {
    // Set CSM if not already set and we have the CSM ID
    if (!customerSM && csm && csmList?.length) {
      const foundCsm = csmList.find((member: any) => member.value === csm);
      if (foundCsm) setCustomerSM(foundCsm);
    }
  }, [customerSM, csm, csmList]);

  useMemo(() => {
    // Set AE if not already set and we have the AE ID
    if (!accountE && ae && csmList?.length) {
      const foundAe = csmList.find((member: any) => member.value === ae);
      if (foundAe) setAccountE(foundAe);
    }
  }, [accountE, ae, csmList]);

  return (
    <div className={`relative ${containerClass || ""}`}>
      {collapsible && (
        <Flex p={16} className="border-b" align="center" justify="space-between">
          <Text size="xs" tt="uppercase" c="text.5" fw={600} lts={0.6}>Key roles</Text>
          <ActionIcon onClick={handlers.toggle} color="text.5" size="xs">
            <ChevronUpIcon style={{ transform: opened ? "rotate(0)" : "rotate(180deg)" }} />
          </ActionIcon>
        </Flex>
      )}
      <Collapse in={opened}>
        <LoadingOverlay loaderProps={{ size: "sm" }} c="main.5" visible={isLoading} overlayProps={{ blur: 2 }} />
        <div className={`${!collapsible ? '' : 'p-2'}`}>
          <div className={`grid ${vertical ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-3'} gap-3`}>
            <div>
              <span className="text-[11px] text-gray-500 mb-1 block">Account Owner</span>
              <AddUserDropdown
                disabled={!accessHelper?.hasPermission(workspace?.id, "can_remove_key_role")}
                value={own}
                placeHolder="Select Account Owner"
                key={uuid()}
                users={ownerList}
                onUserSelect={handleOwnerSelect}
              />
            </div>

            <div>
              <span className="text-[11px] text-gray-500 mb-1 block">CSM</span>
              <AddUserDropdown
                disabled={!accessHelper?.hasPermission(workspace?.id, "can_remove_key_role")}
                value={customerSM}
                placeHolder="Select CSM"
                key={uuid()}
                users={csmList}
                onUserSelect={handleCsmSelect}
              />
            </div>

            <div>
              <span className="text-[11px] text-gray-500 mb-1 block">AE</span>
              <AddUserDropdown
                disabled={!accessHelper?.hasPermission(workspace?.id, "can_remove_key_role")}
                value={accountE}
                placeHolder="Select AE"
                key={uuid()}
                users={csmList}
                onUserSelect={handleAeSelect}
              />
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default KeyRole;
